/**
 * Floating progress banner for a server-built ZIP download. Shows a determinate
 * bar (done/total) while zipping, plus a Cancel button. Direct single-photo
 * downloads are instant, so this only appears for archive jobs.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { IconDownload, IconX } from '@tabler/icons-react';
import type { DownloadPhase } from '../../hooks/useDownload.ts';

interface DownloadProgressProps {
  phase: DownloadPhase;
  done: number;
  total: number;
  onCancel: () => void;
}

export function DownloadProgress({ phase, done, total, onCancel }: DownloadProgressProps) {
  const visible = phase === 'preparing' || phase === 'zipping' || phase === 'canceling';
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const label =
    phase === 'preparing'
      ? 'Preparing download…'
      : phase === 'canceling'
        ? 'Canceling…'
        : total > 0
          ? `Zipping ${done} of ${total}…`
          : 'Zipping…';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          className="fixed bottom-20 left-1/2 z-40 w-[min(92vw,26rem)] -translate-x-1/2 rounded-2xl border border-line bg-surface p-4 shadow-lg"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3">
            <IconDownload className="h-5 w-5 shrink-0 text-accent" />
            <span className="flex-1 truncate text-sm font-medium text-fg">{label}</span>
            {phase !== 'canceling' && (
              <button
                onClick={onCancel}
                aria-label="Cancel download"
                className="-m-2 grid h-11 w-11 place-items-center rounded-lg text-muted duration-200 ease-standard hover:bg-grid"
              >
                <IconX className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-grid">
            <motion.div
              className="h-full rounded-full bg-accent"
              initial={false}
              animate={{ width: `${phase === 'zipping' ? pct : 8}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
